import Card from "common/Boxes/Card";
import Title from "common/Typography/Title";
import React, { ReactNode, useContext, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import useMedia from "utils/hooks/useMedia";
import {
  BasicBotData,
  BotStatus,
  BotType,
  LanguageNameSpaces,
} from "@shared/types";
import {
  getBotExtraDetailsRoute,
  getTradeExtraDetailsRoute,
} from "utils/routes";
import BotChange from "features/bots/common/BotChange";
import ActiveHedgingPreview from "features/bots/MyBots/ActiveHedgingPreview";
import ActiveTradePreview from "features/bots/MyBots/ActiveTradePreview";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import { formatDate, formatDateRelative } from "@shared/utils/date";
import BotActions from "features/bots/common/BotActions";
import TradingViewUUIDs from "features/bots/MyBots/TradingViewUUIDs";
import {
  BotCardPropertyRow,
  BotCardSectionWrapper,
  BottomRowWrapper,
  StatisticTitle,
  StatisticWrapper,
} from "features/bots/MyBots/common/styles";
import MarginData from "features/bots/MyBots/MarginData";
import { typography } from "@shared/common/Themes/typography";
import { BotPreviewCardStyles } from "@shared/features/bots/styles";
import {
  getBotAmountDisplay,
  getBotOneClickFollowCacheKey,
  isGridPnlBotType,
} from "@shared/features/bots/utils";
import {
  CardsSelectorContext,
  ContextProps,
} from "@shared/common/CardsList/CardsSelector.context";
import Checkbox from "common/Checkbox/Checkbox";
import BotStatusTag from "features/bots/common/BotStatus";
import InactiveAccountIcon from "features/configuration/common/InactiveAccountIcon";
import { AccountObjectType } from "@shared/features/settings/types";
import Divider from "common/misc/Divider";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import BotMainIcon from "features/bots/MyBots/common/BotMainIcon";
import { sharedLib } from "@shared/services/shared.service";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { MyBotsPageContext } from "@shared/features/bots/MyBotsPage.context";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { ExchangesIconsGroup } from "features/portfolio/Assets/ExchangesIconsGroup.tsx";
import InactiveGroupIcon from "features/configuration/common/InactiveGroupIcon.tsx";
import { TRADE_EXTRA_DETAILS_TABS } from "@shared/common/constants.ts";
import { TV_ALERT_SYMBOL } from "@shared/features/bots/constants.ts";
import { BOT_PREVIEW_CARD_ID, BOT_PREVIEW_TRADES_SECTION_ID } from "testIds.ts";
import { useCanManageBotType } from "@shared/features/bots/hooks";
import { useSubUserPermissions } from "@shared/utils/hooks/misc.ts";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

interface BotPreviewCardProps {
  botData: BasicBotData;
  id?: string;
}

const BotPreviewCard = ({ botData, id }: BotPreviewCardProps) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const { historyPush } = useHistoryWithQuery();
  const isMobile = useMedia([`(max-width: 500px)`], [true], false);
  const isSmallMobile = useMedia([`(max-width: 400px)`], [true], false);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isEditMode, getCheckboxProps } = useContext<
    ContextProps<BasicBotData> | undefined
  >(CardsSelectorContext)!;
  const { isChannelConfiguration } = useContext(MyBotsPageContext) || {};
  const checkboxProps = getCheckboxProps(botData);
  const { isLoading } =
    sharedLib.sharedApi.bots.useSetDefaultOneClickSignalsBotMutation({
      fixedCacheKey: getBotOneClickFollowCacheKey(botData),
    })[1];

  const isActive = botData.status === BotStatus.ACTIVE;
  const isSignalsBot = botData.bot_type === BotType.SIGNAL;
  const isTradingViewSignalsBot =
    botData.bot_type === BotType.TRADING_VIEW_SIGNALS;
  const isTradingViewBot = botData.bot_type === BotType.TRADING_VIEW;
  const isHedgingBot = botData.bot_type === BotType.HEDGING;
  const useGridPnl = isGridPnlBotType(botData.bot_type);
  const canManageBotType = useCanManageBotType(botData.bot_type);
  const { isReadOnly } = useSubUserPermissions();

  const openBotExtraDetailsDrawer = () => {
    historyPush(
      getBotExtraDetailsRoute(location.pathname, botData.id.toString())
    );
  };

  const onLongPress = () => {
    if (botData.active_trade && location.pathname) {
      historyPush(
        getTradeExtraDetailsRoute(
          location.pathname,
          botData.active_trade.id,
          TRADE_EXTRA_DETAILS_TABS.TIMELINE
        )
      );
    }
  };

  const getCreatedDisplay = () => {
    return (
      <CreatedWrapper $isChannelConfiguration={isChannelConfiguration}>
        {!isBottomNavigator && (
          <Text
            type="secondary"
            $size={typography.fontSizes.S}
            style={{ marginRight: 4 }}
          >
            {t("labels.created")}:
          </Text>
        )}
        <Text type="secondary" $size={typography.fontSizes.S}>
          {formatDate(botData.created_at, false, true)}
        </Text>
      </CreatedWrapper>
    );
  };

  const getCardActions = () => {
    if (isEditMode && canManageBotType) {
      return (
        <CardActions
          onClick={(e: React.MouseEvent) => {
            checkboxProps.onClick();
            e.stopPropagation();
          }}
          style={{ right: 0 }}
        >
          <Checkbox checked={checkboxProps.isSelected} $size={28} />
        </CardActions>
      );
    }

    return (
      <CardActions onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <BotActions bot={botData} isCard={true} />
      </CardActions>
    );
  };

  const getSymbolRow = () => {
    let symbol = botData.symbols?.[0];
    if (!symbol && (isTradingViewBot || isTradingViewSignalsBot)) {
      symbol = TV_ALERT_SYMBOL;
    }
    return (
      <BotCardPropertyRow key="symbol">
        <PropertyTitle style={{ marginRight: 8 }}>
          {t("labels.symbol")}
        </PropertyTitle>
        <BotSymbol>
          <BotMainIcon
            botData={botData}
            size={18}
            iconStyle={BOT_MAIN_ICON_STYLE}
          />
          <Text $ellipsis={true} style={{ color: "inherit" }}>
            {symbol}
          </Text>
        </BotSymbol>
      </BotCardPropertyRow>
    );
  };

  const getGroupRow = () => {
    const groupName = botData.sub_group_name;
    return (
      <BotCardPropertyRow key="group">
        <PropertyTitle style={{ marginRight: 8 }}>
          {t("labels.group")}
        </PropertyTitle>
        <BotSymbol>
          <BotMainIcon
            botData={botData}
            size={18}
            iconStyle={BOT_MAIN_ICON_STYLE}
            isGroupIcon={true}
          />
          <InactiveGroupIcon
            isInactive={isSignalsBot && botData.sub_group_is_active === false}
            style={{ flex: 1, overflow: "hidden" }}
          >
            <Text $ellipsis={true} style={{ color: "inherit" }}>
              {groupName}
            </Text>
          </InactiveGroupIcon>
        </BotSymbol>
      </BotCardPropertyRow>
    );
  };

  const getSymbolOrChannel = () => {
    const rows: ReactNode[] = isSignalsBot ? [getGroupRow()] : [getSymbolRow()];
    if (isTradingViewSignalsBot) rows.unshift(getGroupRow());
    return rows;
  };

  const numClosedTradesData = (
    <>
      {botData.number_of_closed_trades} <BotChange botData={botData} />
    </>
  );
  const numOpenTradesData = (
    <>
      {botData.number_of_open_trades}{" "}
      <BotChange botData={botData} isOpenTrades={true} />
    </>
  );

  const getBotTypeSpecificStatistics = () => {
    if (useGridPnl) {
      return (
        <>
          <BotCardPropertyRow>
            <PropertyTitle>
              {isHedgingBot
                ? t("labels.rebalances")
                : t("labels.take_profits_filled")}
            </PropertyTitle>
            <Text>
              {(isHedgingBot
                ? botData.number_of_rebalances
                : botData.take_profits_filled) || 0}
            </Text>
          </BotCardPropertyRow>
          <BotCardPropertyRow>
            <PropertyTitle>{t("labels.realized_pnl")}</PropertyTitle>
            <Text as="div">
              <BotChange botData={botData} isRealizedPnl={true} />
            </Text>
          </BotCardPropertyRow>
        </>
      );
    } else if (isSignalsBot) {
      return (
        <BotCardPropertyRow>
          <PropertyTitle>{t("labels.last_trade_opened")}</PropertyTitle>
          <Text>
            {botData.last_trade
              ? formatDateRelative(botData.last_trade, true, true, true)
              : t("common.na")}
          </Text>
        </BotCardPropertyRow>
      );
    } else if (!isTradingViewSignalsBot) {
      return (
        <>
          <BotCardPropertyRow>
            <PropertyTitle>{t("labels.closed_trades")}</PropertyTitle>
            <Text style={{ display: "flex" }} as="div">
              {numClosedTradesData}
            </Text>
          </BotCardPropertyRow>
        </>
      );
    }
  };

  const getBottomRow = () => {
    if (isChannelConfiguration) return;
    if (botData.bot_type === BotType.TRADING_VIEW || isTradingViewSignalsBot) {
      if (!isReadOnly) {
        return <TradingViewUUIDs isMobile={isMobile} botData={botData} />;
      }
      return;
    } else if (isSignalsBot) {
      return (
        <>
          <Divider text={t("tabs.trades")} />
          <BottomRowWrapper
            data-testid={BOT_PREVIEW_TRADES_SECTION_ID}
            $useActiveTradeRows={isSmallMobile}
          >
            <StatisticWrapper $useActiveTradeRows={isSmallMobile}>
              <StatisticTitle>{t("labels.closed_trades")}</StatisticTitle>
              <StatisticValue style={{ marginTop: 2 }}>
                {numClosedTradesData}
              </StatisticValue>
            </StatisticWrapper>
            <StatisticWrapper $useActiveTradeRows={isSmallMobile}>
              <StatisticTitle>{t("labels.open_trades")}</StatisticTitle>
              <StatisticValue style={{ marginTop: 2 }}>
                {numOpenTradesData}
              </StatisticValue>
            </StatisticWrapper>
          </BottomRowWrapper>
        </>
      );
    } else if (isHedgingBot) {
      return <ActiveHedgingPreview botData={botData} />;
    } else {
      return <ActiveTradePreview botData={botData} />;
    }
  };

  const botIconStyle = useMemo(
    () => ({
      marginRight: isBottomNavigator ? 12 : 20,
      borderRadius: 8,
    }),
    [isBottomNavigator]
  );

  const botCard = (
    <StyledCard
      onClick={openBotExtraDetailsDrawer}
      onLongPress={botData.active_trade ? onLongPress : undefined}
      data-testid={BOT_PREVIEW_CARD_ID}
      id={id}
    >
      <LoadingSpinnerOverlay isLoading={isLoading}>
        <LogoNameWrapper $isMobile={isMobile}>
          <BotMainIcon
            botData={botData}
            size={isBottomNavigator ? 36 : 50}
            iconStyle={botIconStyle}
            showOneClickDefaultIcon={true}
          />
          <div style={{ paddingRight: 50, overflow: "hidden" }}>
            <Flex style={{ marginBottom: isChannelConfiguration ? 12 : 0 }}>
              <Text
                $size={
                  isBottomNavigator
                    ? typography.fontSizes.L
                    : typography.fontSizes.XL
                }
                $ellipsis={true}
              >
                {botData.name}
              </Text>
              <BotStatusTag hideText={isBottomNavigator} bot={botData} />
            </Flex>
            {!isChannelConfiguration && (
              <>
                <InactiveAccountIcon
                  hideIcon={true}
                  accountId={botData.client_id}
                  objectType={AccountObjectType.ACCOUNT}
                  validColor={theme.colors.secondaryGrey}
                >
                  <Text
                    $size={
                      isBottomNavigator
                        ? typography.fontSizes.S
                        : typography.fontSizes.L
                    }
                    style={{
                      position: "relative",
                      top: -1,
                      color: "inherit",
                    }}
                  >
                    {botData.client_name}
                  </Text>
                </InactiveAccountIcon>
                <MarginData botData={botData} />
              </>
            )}
          </div>
          {getCardActions()}
          {getCreatedDisplay()}
        </LogoNameWrapper>
        <BotCardSectionWrapper $isMobile={isMobile}>
          {getSymbolOrChannel()}
          {!isChannelConfiguration && !isTradingViewSignalsBot && (
            <>
              <BotCardPropertyRow>
                <PropertyTitle>{t("labels.amount")}</PropertyTitle>
                <Text>{getBotAmountDisplay(botData)}</Text>
              </BotCardPropertyRow>
              {getBotTypeSpecificStatistics()}
            </>
          )}
          {isTradingViewSignalsBot && (
            <BotCardPropertyRow>
              <PropertyTitle>{t("labels.exchanges")}</PropertyTitle>
              <ExchangesIconsGroup
                exchanges={botData.exchanges}
                size={28}
                maxCount={7}
              />
            </BotCardPropertyRow>
          )}
        </BotCardSectionWrapper>
        {getBottomRow()}
      </LoadingSpinnerOverlay>
    </StyledCard>
  );

  return useMemo(
    () => botCard,
    [
      botData,
      isMobile,
      isActive,
      id,
      isEditMode,
      checkboxProps.isSelected,
      checkboxProps.errorMessage,
      isLoading,
      isBottomNavigator,
      isSmallMobile,
      isReadOnly,
      i18n.language,
    ]
  );
};

export default React.memo(BotPreviewCard);

const BOT_MAIN_ICON_STYLE = { marginRight: 8, borderRadius: 4 };

const StyledCard = styled(Card)`
  ${BotPreviewCardStyles.card};
`;
const LogoNameWrapper = styled.div<{ $isMobile?: boolean }>`
  ${BotPreviewCardStyles.logoNameWrapper};
  position: relative;
`;
const BotSymbol = styled.div`
  ${BotPreviewCardStyles.symbol};
`;
const CardActions = styled.div`
  ${BotPreviewCardStyles.cardActions};
`;
const CreatedWrapper = styled.div<{ $isChannelConfiguration?: boolean }>`
  ${BotPreviewCardStyles.createdWrapper};
  position: absolute;
  bottom: 12px;
  right: 0;
`;
const StatisticValue = styled(Text).attrs({
  $size: typography.fontSizes.M,
  $weight: typography.fontWeights.MEDIUM,
  as: "div",
})`
  display: flex;
  align-items: center;
`;
const PropertyTitle = styled(Title).attrs({ type: "secondary" })`
  font-size: ${(p) => p.theme.typography.fontSizes.M};
  @media (max-width: ${phoneMaxWidth}) {
    font-size: ${(p) => p.theme.typography.fontSizes.S};
  }
`;
